import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';

import { IRfqLot } from './../rfq/interfaces/rfq-lot.interface';
import { IPaginationResponse } from './../shared/interfaces/paginator.interface';
import { LocationApiService } from './../shared/services/api/location/location-api.service';
import { LotApiService } from './../shared/services/api/lot-api.service';
import { ILocationOld, ILocationState } from './interfaces/location.interface';
import { LocationStateService } from './location-state.service';

@Injectable({
  providedIn: 'root',
})
export class LocationFacadeService {
  private cancelSubject = new Subject<void>();

  constructor(
    private locationStateService :LocationStateService,
    private locationApiService :LocationApiService,
    private lotApiService :LotApiService,
  ) {}

  getLocationsV1(
    params :{ [param :string] :string | string[] } = {},
  ) :Observable<IPaginationResponse> {
    this.locationStateService.updateState({ loading: true });

    return this.locationApiService.get(params).pipe(
      takeUntil(this.cancelSubject),
      finalize(() => this.locationStateService.updateState({ loading: false })),
      tap((response) => {
        this.locationStateService.updateState({ locations: response.data });
      }),
    );
  }

  getRfqLotsIds(params :{
    [param :string] :string | string[];
  }) :Observable<IPaginationResponse> {
    this.locationStateService.updateState({ loading: true });

    return this.lotApiService.getByRfqIds(params).pipe(
      finalize(() => this.locationStateService.updateState({ loading: false })),
      tap((response) => {
        this.locationStateService.updateState({ lots: response.data });
      }),
    );
  }

  setCurrentQueryParams(params :{ [param :string] :string | string[] }) :void {
    this.locationStateService.updateState({ currentQueryParams: params });
    this.getLocationsV1(params).subscribe();
  }

  updateState(newState :Partial<ILocationState>) :void {
    this.locationStateService.updateState({
      ...this.locationStateService.state,
      ...newState,
    });
  }

  cancelRequest() {
    this.cancelSubject.next();
    this.cancelSubject.complete();
    this.cancelSubject = new Subject<void>(); // Reset the subject for future use
  }

  get state() :ILocationState {
    return this.locationStateService.state;
  }

  get loading$() :Observable<boolean> {
    return this.locationStateService.loading$;
  }

  get locations$() :Observable<ILocationOld[]> {
    return this.locationStateService.locations$;
  }

  get lots$() :Observable<IRfqLot[]> {
    return this.locationStateService.lots$;
  }

  get currentQueryParams() :{ [param :string] :string | string[] } {
    return this.locationStateService.currentQueryParams;
  }
}
